/**
 * Configuration and constants for recurring transaction detection
 * Centralizes all magic numbers and thresholds for easy tuning
 */

export const DETECTION_CONFIG = {
  /**
   * Frequency detection ranges (in days)
   * Each frequency has min/max bounds for interval matching
   */
  frequencies: {
    weekly: { min: 6, max: 8, label: "weekly" as const },
    biweekly: { min: 13, max: 15, label: "biweekly" as const },
    monthly: { min: 25, max: 35, label: "monthly" as const },
    quarterly: { min: 85, max: 95, label: "quarterly" as const },
    yearly: { min: 360, max: 370, label: "yearly" as const },
  },

  /**
   * Detection thresholds and tolerances
   */
  thresholds: {
    /** Minimum number of occurrences to consider a pattern recurring */
    minOccurrences: 3,

    /** Days of tolerance for interval consistency (±N days) */
    intervalTolerance: 5,

    /** Percentage amount variance allowed (0.20 = 20%) */
    amountTolerance: 0.2,

    /** Minimum days between occurrences (avoid daily transactions) */
    minInterval: 20,

    /** Maximum days between occurrences for monthly patterns */
    maxInterval: 35,

    /** Maximum allowed skipped payments before rejecting pattern */
    maxSkippedPayments: 2,

    /** Maximum reasonable price increase (0.40 = 40%) */
    priceIncreaseThreshold: 0.4,

    /** Multiplier for "possibly ended" detection (1.5x expected interval) */
    possiblyEndedMultiplier: 1.5,

    /** Days before due date to show "due soon" status */
    dueSoonDays: 7,

    /** Minimum length for merchant name after normalization */
    minMerchantLength: 3,

    /** Maximum number of significant words to extract from merchant name */
    maxMerchantWords: 3,
  },

  /**
   * Skipped payment detection configuration
   * Helps identify when a subscription missed 1-2 cycles vs. being cancelled
   */
  skipPaymentDetection: {
    /** Minimum days to consider as potential skipped payment (2x monthly) */
    minDays: 55,

    /** Maximum days to consider as potential skipped payment (3x monthly) */
    maxDays: 100,

    /** Base interval for monthly subscriptions */
    baseInterval: 30,
  },

  /**
   * Reactivation detection configuration
   * Handles subscriptions that were cancelled and then reactivated
   * (e.g., Netflix cancelled for 6 months, then resumed)
   */
  reactivationDetection: {
    /** Enable reactivation detection */
    enabled: true,

    /** Minimum days to consider as a potential reactivation gap (4 months) */
    minGapDays: 120,

    /** Minimum intervals before the gap to establish a pattern */
    minBeforeGap: 2,

    /** Minimum intervals after the gap to confirm reactivation */
    minAfterGap: 2,

    /** Maximum number of reactivation gaps allowed in a pattern */
    maxReactivations: 1,
  },

  /**
   * Description normalization patterns
   * Patterns to remove from transaction descriptions for better grouping
   */
  normalization: {
    /** Remove long numbers (transaction IDs, reference numbers) */
    longNumberPattern: /\d{4,}/g,

    /** Remove short numbers too for better grouping */
    shortNumberPattern: /\d{1,3}/g,

    /** Remove dates in various formats */
    datePattern: /\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/g,

    /** Replace special characters with spaces */
    specialCharsPattern: /[^\w\s]/g,

    /** Collapse multiple spaces */
    multiSpacePattern: /\s+/g,

    /** Generic descriptions to skip */
    skipDescriptions: ["payment", "purchase", "debit", "transaction"],
  },

  /**
   * Bill detection keywords
   * If description contains these, classify as "bill" instead of "subscription"
   */
  billKeywords: [
    "electric",
    "electricity",
    "gas",
    "water",
    "sewer",
    "internet",
    "phone",
    "mobile",
    "insurance",
    "rent",
    "mortgage",
    "utilities",
    "utility",
  ],

  /**
   * Confidence scoring weights
   * How much each factor contributes to overall confidence score
   */
  confidenceWeights: {
    interval: 0.4, // 40% weight on interval consistency
    amount: 0.4, // 40% weight on amount consistency
    occurrence: 0.2, // 20% weight on number of occurrences
  },
} as const;

export type RecurringFrequency = 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly';
export type RecurringStatus = 'not_due' | 'due_soon' | 'due_today' | 'overdue' | 'possibly_ended';
export type RecurringType = 'subscription' | 'bill';
